import React from "react";
import ExlyModal from "common/Components/ExlyModal";
import { ExlyNote } from "common/Components/ExlyNote/ExlyNote";
import { ExlyInput } from "common/form";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../../constants/ZoomAttendance.constants";
import { useReconciliationModal } from "../../hooks/useReconciliationModal";

export const ReconciliationModal = ({
  open,
  onClose,
  participant,
  listingUuid,
  slotUuid,
  onReconcileSuccess,
  onCreateNewBooking,
}) => {
  const {
    searchQuery,
    selectedBookingUuid,
    bookings,
    searchLoading,
    submitLoading,
    handleSearchChange,
    handleSelectBooking,
    handleConfirmMapping,
  } = useReconciliationModal({
    open,
    slotUuid,
    listingUuid,
    participant,
    onReconcileSuccess,
    onClose,
  });

  if (!participant) return null;

  const participantName =
    participant.customer_name || participant.name || "Participant";
  const participantEmail =
    participant.customer_email || participant.user_email || participant.email;
  const participantJoinTime = participant.join_time || participant.start_time;
  const participantDuration =
    participant.duration !== undefined
      ? participant.duration
      : participant.total_minutes_attended;

  const handleCreateNewBookingClick = () => {
    onClose();
    if (onCreateNewBooking) {
      onCreateNewBooking(participant);
    }
  };

  const handleSearchInputChange = (val) => {
    const rawVal = typeof val === "object" ? val?.target?.value : val;
    handleSearchChange({ target: { value: rawVal } });
  };

  const renderSearchResults = ({
    searchLoading,
    bookings,
    selectedBookingUuid,
    handleSelectBooking,
  }) => {
    if (searchLoading) {
      return (
        <div className="tw-py-6 tw-text-center tw-text-xs tw-text-gray-500">
          {ZOOM_ATTENDANCE_CONSTANTS.LABELS.SEARCHING_BOOKINGS}
        </div>
      );
    }

    if (bookings && bookings.length > 0) {
      return bookings.map((customer) => {
        const isSelected = selectedBookingUuid === customer.customer_uuid;
        return (
          <div
            key={customer.customer_uuid}
            onClick={() => handleSelectBooking(customer)}
            className={`tw-flex tw-items-center tw-gap-3.5 tw-p-3.5 tw-cursor-pointer tw-transition-colors ${
              isSelected ? "tw-bg-[#F3F0FF]" : "tw-bg-white hover:tw-bg-gray-50"
            }`}
          >
            {/* Radio Circle */}
            <div className="tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
              <div
                className={`tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-border-solid tw-flex tw-items-center tw-justify-center tw-transition-colors ${
                  isSelected
                    ? "tw-border-[#4F46E5]"
                    : "tw-border-gray-400 tw-bg-white"
                }`}
              >
                {isSelected && (
                  <div className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-[#4F46E5]" />
                )}
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-0.5">
              <span className="tw-text-sm tw-font-bold tw-text-gray-900 tw-font-serif">
                {customer.customer_name}
              </span>
              <div className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5">
                {customer.customer_email && (
                  <span>{customer.customer_email}</span>
                )}
                {customer.customer_email && customer.customer_phone && (
                  <span>·</span>
                )}
                {customer.customer_phone && (
                  <span>{customer.customer_phone}</span>
                )}
              </div>
            </div>
          </div>
        );
      });
    }

    return (
      <div className="tw-py-6 tw-flex tw-flex-col tw-items-center tw-gap-2 tw-bg-white">
        <span className="tw-text-xs tw-text-gray-500">
          {ZOOM_ATTENDANCE_CONSTANTS.LABELS.NO_BOOKINGS_FOUND}
        </span>
      </div>
    );
  };

  return (
    <ExlyModal
      open={open}
      onClose={onClose}
      title={ZOOM_ATTENDANCE_CONSTANTS.LABELS.RECONCILE_UNRECOGNIZED_TITLE}
      primaryBtnText={
        submitLoading
          ? ZOOM_ATTENDANCE_CONSTANTS.LABELS.MAPPING_IN_PROGRESS
          : ZOOM_ATTENDANCE_CONSTANTS.LABELS.CONFIRM_MAPPING
      }
      onPrimaryBtnClick={handleConfirmMapping}
      primaryBtnProps={{
        disabled: !selectedBookingUuid || submitLoading,
      }}
      showSecondaryBtn={true}
      secondaryBtnText={ZOOM_ATTENDANCE_CONSTANTS.LABELS.CANCEL}
      onSecondaryBtnClick={onClose}
      secondaryBtnProps={{
        disabled: submitLoading,
      }}
    >
      <div className="tw-py-5 tw-flex tw-flex-col tw-gap-4 tw-max-w-lg tw-w-full">
        {/* Top Zoom Participant Card */}
        <div className="tw-bg-[#F8F7F4] tw-rounded-xl tw-p-4 tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-[11px] tw-font-semibold tw-tracking-wider tw-text-gray-400 tw-uppercase">
            ZOOM PARTICIPANT
          </span>
          <span className="tw-text-base tw-font-serif tw-font-bold tw-text-gray-900">
            {participantName}
          </span>
          <div className="tw-text-xs tw-text-gray-600 tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
            {participantEmail && <span>{participantEmail}</span>}
            {participantEmail && participantJoinTime && <span>·</span>}
            {participantJoinTime && <span>joined {participantJoinTime}</span>}
            {(participantEmail || participantJoinTime) &&
              participantDuration !== undefined && <span>·</span>}
            {participantDuration !== undefined && (
              <span>{participantDuration} min</span>
            )}
          </div>
        </div>

        {/* Search Field */}
        <div className="tw-flex tw-flex-col tw-gap-1">
          <label className="tw-text-xs tw-font-semibold tw-text-gray-700">
            {ZOOM_ATTENDANCE_CONSTANTS.LABELS.SEARCH_BOOKINGS_LABEL}
          </label>
          <ExlyInput
            type="text"
            placeholder={
              ZOOM_ATTENDANCE_CONSTANTS.LABELS.SEARCH_BOOKINGS_PLACEHOLDER
            }
            value={searchQuery}
            onChange={handleSearchInputChange}
            className="tw-w-full tw-h-9 tw-px-3 tw-bg-white tw-border tw-border-solid tw-border-gray-300 focus:tw-border-indigo-500 tw-rounded-md tw-text-xs tw-text-gray-800 tw-outline-none focus:tw-outline-none tw-transition-colors"
          />
        </div>

        {/* Search Results List */}
        <div className="tw-flex tw-flex-col tw-border tw-border-solid tw-border-gray-200 tw-rounded-xl tw-overflow-hidden tw-divide-y tw-divide-gray-100 tw-max-h-56 tw-overflow-y-auto">
          {renderSearchResults({
            searchLoading,
            bookings,
            selectedBookingUuid,
            handleSelectBooking,
          })}
        </div>

        {/* Create New Booking option */}
        <div className="tw-flex tw-items-center tw-justify-between tw-pt-1">
          <span className="tw-text-xs tw-text-gray-600">
            Can&apos;t find matching booking?
          </span>
          <button
            type="button"
            onClick={handleCreateNewBookingClick}
            className="tw-text-xs tw-font-semibold tw-text-indigo-600 hover:tw-text-indigo-800 tw-underline tw-bg-transparent tw-border-none tw-cursor-pointer focus:tw-outline-none"
          >
            {ZOOM_ATTENDANCE_CONSTANTS.LABELS.CREATE_NEW_BOOKING}
          </button>
        </div>

        {/* Warning Banner using ExlyNote */}
        <ExlyNote
          label={ZOOM_ATTENDANCE_CONSTANTS.LABELS.MAP_WARNING_NOTE}
          className="tw-bg-indigo-50 tw-border tw-border-solid tw-border-indigo-100 tw-text-indigo-900"
        />
      </div>
    </ExlyModal>
  );
};
